/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

/** 
  All of the routes for the Material Dashboard 2 React are added here,
  You can add a new route, customize the routes and delete the routes here.

  Once you add a new route on this file it will be visible automatically on
  the Sidenav.

  For adding a new route you can follow the existing routes in the routes array.
  1. The `type` key with the `collapse` value is used for a route.
  2. The `type` key with the `title` value is used for a title inside the Sidenav. 
  3. The `type` key with the `divider` value is used for a divider between Sidenav items.
  4. The `name` key is used for the name of the route on the Sidenav.
  5. The `key` key is used for the key of the route (It will help you with the key prop inside a loop).
  6. The `icon` key is used for the icon of the route on the Sidenav, you have to add a node.
  7. The `collapse` key is used for making a collapsible item on the Sidenav that has other routes
  inside (nested routes), you need to pass the nested routes inside an array as a value for the `collapse` key.
  8. The `route` key is used to store the route location which is used for the react router.
  9. The `href` key is used to store the external links location.
  10. The `title` key is only for the item with the type of `title` and its used for the title text on the Sidenav.
  10. The `component` key is used to store the component of its route.
*/

// Material Dashboard 2 React layouts
import Dashboard from "layouts/admin/dashboard";
import MyCompetencies from "layouts/admin/Competencies/myCompetencies";

// @mui icons
import Icon from "@mui/material/Icon";
import ProtectedRoute from "components/ProtectedRoute";
import SignOut from "layouts/authentication/logout";
import KpisView from "layouts/admin/kpis";
import OrganizationPage from "layouts/admin/Organization";
import StrategicInfo from "layouts/admin/StrategicInfo/index";
import ImportReview from "components/ImportReview";
import JobDetails from "layouts/admin/Organization/components/JobView/components/JobDetails";
import Overview from "layouts/user/profile";
import PolicyViewer from "layouts/admin/policies";
import PolicyEditor from "layouts/admin/policies/PolicyEditor";
import SuccessionPlanSteps from "layouts/admin/SuccessionPlanSteps";
import HoursCalculator from "layouts/user/WLA/ManpowerPlanning/HoursCalculator";
import LearningAndDevelopment from "layouts/admin/LearningAndDevelopment";
import TrainingsTabView from "layouts/admin/LearningAndDevelopment";
import TalentAcquisitionPage from "layouts/admin/talentAcquisition";
import RequisitionDetails from "layouts/admin/talentAcquisition/RequisitionDetails";
import ExternalForm from "layouts/admin/talentAcquisition/ExternalForm";
import MyScorecard from "layouts/user/KPIs/myScorecard";
import SuperAdminPanel from "layouts/super admin/homepage";
import ManageCompany from "layouts/super admin/manageCompany";
import ErrorLogsPage from "layouts/super admin/logsPage";
import SupportTicketsPage from "layouts/super admin/supportTickets";
import EmployeeDetails from "layouts/admin/Organization/components/EmployeesView/components/EmployeeDetails";
import Successors from "layouts/admin/SuccessionPlanSteps/components/Successors";
import SuccessionSteps from "layouts/admin/SuccessionPlanSteps/components/SuccessionSteps";
import PayrollPage from "layouts/admin/Payroll";

const getRoutess = (t, role, modules) => {
  const safeModules = modules && typeof modules === "object" ? Object.keys(modules) : [];
  const allRoutes = [
    {
      type: "collapse",
      name: t("routes.dashboard"),
      key: "dashboard",
      icon: <Icon fontSize="small">dashboard</Icon>,
      route: "/dashboard",
      component: (
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      ),
    },
    {
      type: "collapse",
      name: t("routes.strategic"),
      module: "Strategic Info",
      key: "strategic",
      icon: <Icon fontSize="small">star</Icon>,
      route: "/strategic",
      component: (
        <ProtectedRoute>
          <StrategicInfo />
        </ProtectedRoute>
      ),
    },

    {
      type: "collapse",
      name: t("routes.organization"),
      module: "Organization",
      key: "organization",
      icon: <Icon fontSize="small">apartment</Icon>,
      route: "/organization",
      component: (
        <ProtectedRoute>
          <OrganizationPage />
        </ProtectedRoute>
      ),
    },
    {
      type: "collapse",
      name: t("routes.policies"),
      module: "Policies",
      key: "policies",
      icon: <Icon fontSize="small">gavel</Icon>,
      route: "/policies",
      component: (
        <ProtectedRoute>
          <PolicyViewer />
        </ProtectedRoute>
      ),
    },
    {
      type: "hidden",
      name: t("routes.policies"),
      module: "Policies",
      key: "policy",
      icon: <Icon fontSize="small">gavel</Icon>,
      route: "/policy",
      component: (
        <ProtectedRoute>
          <PolicyEditor />
        </ProtectedRoute>
      ),
    },
    {
      type: "collapse",
      name: t("routes.performance"),
      module: "Performance",
      key: "performance",
      icon: <Icon fontSize="small">leaderboard</Icon>,
      route: "/performance",
      component: (
        <ProtectedRoute>
          <KpisView />
        </ProtectedRoute>
      ),
    },
    {
      type: "hidden",
      name: "Import",
      key: "import",
      icon: <Icon fontSize="small">leaderboard</Icon>,
      route: "/import",
      component: (
        <ProtectedRoute>
          <ImportReview />
        </ProtectedRoute>
      ),
    },

    {
      type: "hidden",
      name: "Job Details",
      module: "Organization",
      key: "jobDetails",
      icon: <Icon fontSize="small">leaderboard</Icon>,
      route: "/job-details",
      component: (
        <ProtectedRoute>
          <JobDetails />
        </ProtectedRoute>
      ),
    },
    {
      type: "collapse",
      name: t("routes.competencies"),
      module: "Competencies",
      key: "competencies",
      icon: <Icon fontSize="small">school</Icon>,
      route: "/competencies",
      component: (
        <ProtectedRoute>
          <MyCompetencies />
        </ProtectedRoute>
      ),
    },
    {
      type: "collapse",
      name: t("uroutes.trainings"),
      module: "Trainings",
      key: "learning",
      icon: <Icon fontSize="small">workspace_premium</Icon>,
      route: "/learning",
      component: (
        <ProtectedRoute>
          <TrainingsTabView />
        </ProtectedRoute>
      ),
    },
    {
      type: "collapse",
      name: t("admin.successionPlan.title"),
      module: "Succession Plan Steps",
      key: "successionPlanSteps",
      icon: <Icon fontSize="small">emoji_events</Icon>,
      route: "/successionPlanSteps",
      component: (
        <ProtectedRoute>
          <SuccessionPlanSteps />
        </ProtectedRoute>
      ),
    },
    {
      type: "collapse",
      name: t("routes.talentAcquisition"),
      module: "Talent Acquisition",
      key: "recruiting",
      icon: <Icon fontSize="small">person_search</Icon>,
      route: "/recruiting",
      component: (
        <ProtectedRoute>
          <TalentAcquisitionPage />
        </ProtectedRoute>
      ),
    },

    // routes.js - Add this to your route configuration
    {
      type: "collapse",
      name: t("routes.payroll"),
      module: "Payroll",
      key: "payroll",
      icon: <Icon fontSize="small">payments</Icon>,
      route: "/payroll",
      component: (
        <ProtectedRoute>
          <PayrollPage />
        </ProtectedRoute>
      ),
    },

    {
      type: "hidden",
      category: t("routes.talentAcquisition"),

      name: "Requisition Details",
      key: "recruiting/details",
      module: "Talent Acquisition",
      icon: <Icon fontSize="small">person_search</Icon>,
      route: "/recruiting/details",
      component: (
        <ProtectedRoute>
          <RequisitionDetails />
        </ProtectedRoute>
      ),
    },

    {
      type: "hidden",
      category: t("uroutes.profile"),
      name: t("uroutes.profile"),
      key: "profile",
      icon: <Icon fontSize="small">leaderboard</Icon>,
      route: "/profile",
      component: (
        <ProtectedRoute>
          <Overview />
        </ProtectedRoute>
      ),
    },

    {
      type: "hidden",
      category: t("uroutes.manpowerPlanning"),

      name: "Hours Calculator",
      key: "hours-calculator",
      module: "Organization",
      icon: <Icon fontSize="small">calculate</Icon>,
      route: "/hours-calculator",
      component: (
        <ProtectedRoute>
          <HoursCalculator />
        </ProtectedRoute>
      ),
    },
    {
      type: "hidden",
      name: t("uroutes.othersScorecard"),
      key: "other's scorecard",
      module: "Performance",
      icon: <Icon fontSize="small">assessment</Icon>,
      route: "/team/scorecard",
      component: (
        <ProtectedRoute>
          <MyScorecard />
        </ProtectedRoute>
      ),
    }, // In your routes array
    {
      type: "hidden", // This hides it from the Sidebar
      name: "Super Admin Panel",
      key: "super-admin",
      route: "/super-admin",
      component: (
        // Ensure you wrap this in a check so normal HR admins can't access it via URL
        <ProtectedRoute>
          <SuperAdminPanel />
        </ProtectedRoute>
      ),
    },
    {
      type: "hidden", // Hidden from sidebar
      name: "Manage Company",
      key: "manage-company",
      route: "/super-admin/manage",
      component: (
        // Add your ProtectedRoute wrapper here if needed
        <ProtectedRoute>
          <ManageCompany />
        </ProtectedRoute>
      ),
    },
    {
      type: "hidden", // Hidden from sidebar
      name: "Error Logs",
      key: "error-logs",
      route: "/super-admin/logs",
      component: (
        // Add your ProtectedRoute wrapper here if needed
        <ProtectedRoute>
          <ErrorLogsPage />
        </ProtectedRoute>
      ),
    },
    {
      type: "hidden", // Hidden from sidebar
      name: "Support Tickets",
      key: "support-tickets",
      route: "/super-admin/tickets",
      component: (
        // Add your ProtectedRoute wrapper here if needed
        <ProtectedRoute>
          <SupportTicketsPage />
        </ProtectedRoute>
      ),
    },
    {
      type: "collapse",
      name: t("routes.signOut"),
      key: "sign-out",
      icon: <Icon fontSize="small">logout</Icon>,
      route: "/authentication/sign-out",
      component: <SignOut />,
    },
    {
      type: "hidden",
      name: "Employee Details",
      module: "Organization",
      key: "employeeDetails",
      icon: <Icon fontSize="small">leaderboard</Icon>,
      route: "/employee-details",
      component: (
        <ProtectedRoute>
          <EmployeeDetails />
        </ProtectedRoute>
      ),
    },
    {
      type: "hidden",
      category: t("uroutes.successors"),

      name: "Successors",
      key: "successors",
      module: "Organization",
      icon: <Icon fontSize="small">calculate</Icon>,
      route: "/successors",
      component: (
        <ProtectedRoute>
          <Successors />
        </ProtectedRoute>
      ),
    },
    {
      type: "hidden",
      category: t("routes.successionSteps"),

      name: "SuccessionSteps",
      key: "successionSteps",
      module: "Organization",
      icon: <Icon fontSize="small">calculate</Icon>,
      route: "/succession-steps",
      component: (
        <ProtectedRoute>
          <SuccessionSteps />
        </ProtectedRoute>
      ),
    },
  ];
  return allRoutes.filter(
    (r) =>
      r.key === "dashboard" ||
      r.key === "sign-out" ||
      r.key === "profile" ||
      role === 1 ||
      safeModules.includes(r.module)
  );
};

export default getRoutess;
